import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { compose } from 'redux';
import { Satellite } from '@material-ui/icons';
import { DEFAULT_RISKS } from '../../Components/Common/Config/Risks';

function withSearchResults (WrappedComponent) {
    class WithSearchResults extends React.Component {
        static propTypes = {
            activeLayers: PropTypes.array.isRequired,
            // Redux state
            enabled: PropTypes.bool.isRequired,
            search: PropTypes.object,
            searchSource: PropTypes.number,
            searchAreaFacet: PropTypes.object,
            searchAreaFacetValue: PropTypes.string,
            searchAreaValid: PropTypes.bool,
            analysis: PropTypes.object,
            layers: PropTypes.array.isRequired,
            highlightedFeatures: PropTypes.array.isRequired
        };

        render () {
            const { activeLayers, enabled, search, searchSource, analysis, layers, highlightedFeatures, searchAreaFacet, searchAreaValid, searchAreaFacetValue, ...props } = this.props;
            const newActiveLayers = [...activeLayers];

            // Todo check if on search tab
            // If panel is open and on site finder
            // If on SiteFinder and search results
            if (enabled) {
                // If search area is valid and is fixed (references a layer) then load tiles for that layer
                if (searchAreaValid && searchAreaFacet?.layerType === 'fixed') {
                    newActiveLayers.push({
                        uid: 'search_area_facet_' + searchAreaFacet.id,
                        systemName: searchAreaFacet.layer.systemName,
                        visible: true,
                        printable: false,
                        layerData: {
                            mvt: true,
                            url: `${searchAreaFacet.layerData.url}?value=${searchAreaFacetValue}`
                        },
                        styling: {
                            'layers': [
                                {
                                    'type': 'fill',
                                    'paint': {
                                        'fill-color': '#A9FE95',
                                        'fill-outline-color': '#A9FE95',
                                        'fill-opacity': 0.2

                                    }
                                },
                                {
                                    'type': 'line',
                                    'layout': {
                                        'line-cap': 'round',
                                        'line-join': 'round'
                                    },
                                    'paint': {
                                        'line-color': '#A9FE95',
                                        'line-dasharray': [0.2, 2],
                                        'line-width': 4
                                    }
                                }
                            ],
                            'source': {
                                'type': 'vector',
                                'maxzoom': 22,
                                'minzoom': 1
                            }
                        },
                        settings: [],
                        primaryKey: 'id',
                        highlights: []
                    });
                }
                // If want to progressively show search results then need to make sure than minzoom/maxzoom update with the amount currently
                // Think those fields do not trigger a state update
                if (search && search.status === 'complete' && search.computedData && search.computedData.length > 0) {
                    const layer = layers.find(layer => layer.id === searchSource);
                    const idProperty = layer.properties.id;
                    if (analysis) {
                        // Same as the other except style based on the colour of the score
                        // Need to extract score value and weighting profile nonsense
                        const colourMap = DEFAULT_RISKS.map(e => []);

                        for (const fa of analysis.featureAnalyses) {
                            if (fa.status !== 'complete') {
                                continue;
                            }
                            const idx = DEFAULT_RISKS.findIndex(risk => risk.label === fa.computedData.weightings.risk);
                            colourMap[idx].push(fa.computedData.feature.id);
                        }

                        /* This feels like it belongs in the backend to an extent by adding an extra column with the score as
                        an index on the colour map. To do this, the backend query would need to be expanded to join the score
                        data as inline values onto the columns returned. This styling could then use a simple match statement
                        as opposed to using the polygons ids in an in statement. This hasn't been done due to the complexity of
                        changes on the backend versus these two lines below. */
                        const styler = colourMap.map((e, idx) => [['in', ['get', idProperty], ['literal', e]], DEFAULT_RISKS[idx].backgroundColor]).flat();

                        if (styler.length > 0) {
                            newActiveLayers.push({
                                uid: layer.system_name + '_analysis',
                                systemName: layer.system_name,
                                visible: true,
                                printable: false,
                                layerData: analysis.layerData,
                                styling: {
                                    'layers': [
                                        {
                                            'type': 'fill',
                                            'paint': {
                                                'fill-color': [
                                                    'case',
                                                    ...styler,
                                                    '#FFFFFF'
                                                ],
                                                'fill-opacity': 0.35
                                            }
                                        },
                                        {
                                            'type': 'line',
                                            'paint': {
                                                'line-color': [
                                                    'case',
                                                    ...styler,
                                                    '#000000'
                                                ],
                                                'line-width': 3
                                            }
                                        }
                                    ],
                                    'source': {
                                        'type': 'vector',
                                        'maxzoom': 22,
                                        'minzoom': search.computedData.length < 2500 ? 10 : 16 // Dirty to make sure large searches don't show to much
                                    }
                                },
                                settings: [],
                                primaryKey: layer.properties.id,
                                highlights: highlightedFeatures.filter(e => e.layerSystemName === layer.system_name)
                            });
                        }
                    } else {
                        // Else just search results so just show boundaries
                        newActiveLayers.push({
                            uid: layer.system_name + '_search',
                            systemName: layer.system_name,
                            visible: true,
                            printable: false,
                            layerData: {
                                ...search.layerData,
                                url: search.layerData.polygonUrl
                            },
                            styling: {
                                'layers': [
                                    {
                                        'type': 'fill',
                                        'paint': {
                                            'fill-color': 'transparent',
                                            'fill-opacity': 1.0
                                        },
                                        'filter': ['==', ['geometry-type'], 'Polygon'],
                                        'maxzoom': 22,
                                        'minzoom': search.computedData.length < 2500 ? 12 : 16 // Dirty to make sure large searches don't show to much
                                    },
                                    {
                                        'type': 'line',
                                        'paint': {
                                            'line-color': '#FF0000',
                                            'line-width': 2
                                        },
                                        'filter': ['==', ['geometry-type'], 'Polygon'],
                                        'maxzoom': 22,
                                        'minzoom': search.computedData.length < 2500 ? 12 : 16 // Dirty to make sure large searches don't show to much                
                                    },
                                    {
                                        'type': 'symbol',
                                        'paint': {
                                            'icon-color': '#9ED6DE',
                                            'icon-halo-color': '#000000',
                                            'icon-halo-width': 3
                                        },
                                        'layout': {
                                            'icon-image': 'pin',
                                            'icon-size': 1,
                                            'icon-anchor': 'bottom',
                                            'icon-allow-overlap': true,
                                            'icon-ignore-placement': true
                                        },
                                        'filter': ['==', ['geometry-type'], 'Point'],
                                        'maxzoom': 22,
                                        'minzoom': search.computedData.length < 2500 ? 12 : 16 // Dirty to make sure large searches don't show to much
                                    }
                                ],
                                'source': {
                                    'type': 'vector',
                                    'maxzoom': 22,
                                    'minzoom': search.computedData.length < 2500 ? 12 : 16 // Dirty to make sure large searches don't show to much
                                },
                                'highlights': [
                                    {
                                        'type': 'line',
                                        'paint': {
                                            'line-color': 'rgb (0, 255, 255)',
                                            'line-width': 6
                                        }
                                    }
                                ]
                            },
                            settings: [],
                            primaryKey: layer.properties.id,
                            highlights: highlightedFeatures.filter(e => e.layerSystemName === layer.system_name)
                        });
                        newActiveLayers.push({
                            uid: layer.system_name + '_search_point',
                            systemName: layer.system_name,
                            visible: true,
                            printable: false,
                            layerData: {
                                ...search.layerData,
                                url: search.layerData.pointUrl
                            },
                            styling: {
                                'layers': [
                                    {
                                        'type': 'symbol',
                                        'paint': {
                                            'icon-color': '#9ED6DE',
                                            'icon-halo-color': '#000000',
                                            'icon-halo-width': 3
                                        },
                                        'layout': {
                                            'icon-image': 'pin',
                                            'icon-size': 1,
                                            'icon-anchor': 'bottom',
                                            'icon-allow-overlap': true,
                                            'icon-ignore-placement': true
                                        },
                                        'filter': ['==', ['geometry-type'], 'Point'],
                                        'maxzoom': search.computedData.length < 2500 ? 12 : 16,
                                        'minzoom': 1
                                    }
                                ],
                                'source': {
                                    'type': 'vector',
                                    'maxzoom': search.computedData.length < 2500 ? 12 : 16,
                                    'minzoom': 1
                                },
                                'highlights': [
                                    {
                                        'type': 'line',
                                        'paint': {
                                            'line-color': 'rgb (0, 255, 255)',
                                            'line-width': 6
                                        }
                                    }
                                ]
                            },
                            settings: [],
                            primaryKey: layer.properties.id,
                            highlights: highlightedFeatures.filter(e => e.layerSystemName === layer.system_name)
                        });
                    }
                }
            }

            return (
                <WrappedComponent
                    activeLayers={newActiveLayers}
                    {...props}
                />
            );
        }
    }

    const wrappedComponentName = WrappedComponent.displayName
        || WrappedComponent.name
        || 'Component';

    WithSearchResults.displayName = `withSearchResults(${wrappedComponentName})`;
    return WithSearchResults;
}

const mapStateToProps = (state) => (
    {
        enabled: Boolean(state.panels.panels.search_enhanced.enabled),
        search: state.search.search,
        searchSource: state.search.searchArea.layerId,
        searchAreaFacet: state.lookups.uiData.facetChoices.find(el => el.id === state.search.searchArea.facetId),
        searchAreaFacetValue: state.search.searchArea.facetValue,
        searchAreaValid: state.search.searchArea.facetValid,
        analysis: state.analysis.analysis,
        layers: state.layers.layers,
        highlightedFeatures: state.highlights.highlightedFeatures
    }
);

export default compose(connect(mapStateToProps, null), withSearchResults);